import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/common/DatePicker";
import {
  ArrowLeft,
  CircleUserRound,
  Plus,
  PlusCircle,
  Upload,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { FormField } from "./guests/components/form/FormFields";
import {
  DocumentFormData,
  ReservationFormData,
} from "./guests/utils/reservationSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useReservationSchemas } from "./hooks/useReservationSchemas";
import { CountryField } from "./guests/components/form/CountryField";
import RoomField from "./rooms/components/form/RoomField";
import ImageSelector from "./guests/components/form/ImageSelector";
import { useHotel } from "@/hooks/useHotel";
import { GuestType } from "@/types/guest";
import { DocumentItem } from "./components/DocumentItem";
import AddDocumentModal from "./components/AddDocumentModal";
import { useReservationCreate } from "./hooks/useReservationCreate";
import { isSameDay } from "date-fns";
import PaymentStatusSelect from "./components/form/PaymentStatusSeelct";
import { BillingPaymentStatus } from "@/types/billing-transactions";

export const AddReservationPage: React.FC = () => {
  const navigate = useNavigate();

  const { hotelId, isLoading: isLoadingHotel } = useHotel();
  const { handleReservationSubmit } = useReservationCreate();
  const { reservationSchema } = useReservationSchemas();

  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<ReservationFormData>({
    resolver: zodResolver(reservationSchema),
    defaultValues: {
      additionalGuests: [],
      arrivalDate: new Date().toISOString(),
      departureDate: new Date(
        new Date().setDate(new Date().getDate() + 1),
      ).toISOString(),
    },
  });

  const selectedCountry = watch("language");
  const additionalGuests = watch("additionalGuests") || [];
  const arrivalDate = watch("arrivalDate");
  const departureDate = watch("departureDate");
  const roomId = watch("roomId");
  const selectedImage = watch("photoId");
  const documents = watch("documents");
  const paymentStatus = watch("paymentStatus");

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const addAdditionalGuest = (guestType: GuestType) => {
    const next = [
      ...additionalGuests,
      {
        guestType,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        language: "",
        dateOfBirth: "",
      },
    ];
    setValue("additionalGuests", next);
  };

  const removeAdditionalGuest = (index: number) => {
    setValue(
      "additionalGuests",
      additionalGuests.filter((_, i) => i !== index),
    );
  };

  const handleDocumentSubmit = async (data: DocumentFormData) => {
    setValue("documents", [...(documents || []), data]);
  };

  const removeDocument = (documentId: number) => {
    setValue(
      "documents",
      documents?.filter((_, i) => i !== documentId),
    );
  };

  const onSubmit = async (data: ReservationFormData) => {
    await handleReservationSubmit(data, hotelId!, selectedPhotoFile);
  };

  const handleDateChane = (type: "arrival" | "departure", date: Date) => {
    if (type === "arrival") {
      setValue("arrivalDate", date.toISOString() || "");

      if (isSameDay(departureDate, date)) {
        setValue(
          "departureDate",
          new Date(
            new Date().setDate(new Date(departureDate).getDate() + 1),
          ).toISOString(),
        );
      }
    }

    if (type === "departure") {
      setValue("departureDate", date.toISOString() || "");
    }
  };

  if (isLoadingHotel) {
    return <div>Loading...</div>;
  }

  return (
    <div className="relative min-h-screen p-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/reservations")}
        className="absolute left-12 top-12 flex items-center gap-2 font-semibold"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      <div className="max-w-4xl mx-auto mt-12">
        <h1 className="text-2xl font-semibold text-center">Add reservation</h1>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mt-8">
            <div className="mb-8">
              <h2 className="text-lg font-medium mb-6">Main guest</h2>
              <div className="flex items-start gap-6 mb-6">
                <ImageSelector
                  defaultIcon={
                    <CircleUserRound className={"size-[72px] stroke-1"} />
                  }
                  overlayContent={
                    <div
                      className={
                        "rounded-full bg-white absolute bottom-0 -translate-x-1/2 left-1/2"
                      }
                    >
                      <PlusCircle className={"size-5"} />
                    </div>
                  }
                  onImageSelect={handleImageUpload}
                  selectedImage={selectedImage || undefined}
                  className="mb-4"
                  imageClassName={"size-[72px] rounded-full"}
                />
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    id="firstName"
                    label="NAME"
                    type="text"
                    required
                    register={register}
                    error={errors?.firstName?.message}
                    placeholder="Enter first name"
                  />

                  <FormField
                    id="lastName"
                    label="SURNAME"
                    type="text"
                    required
                    register={register}
                    error={errors?.lastName?.message}
                    placeholder="Enter surname"
                  />
                </div>

                <div className="flex gap-3 self-center">
                  <div className="flex flex-col items-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-6 h-6 border-2 border-black rounded-full p-0 transition-transform active:scale-90"
                      onClick={() => addAdditionalGuest(GuestType.ADULT)}
                    >
                      <Plus className="h-2 w-2" />
                    </Button>
                    <span className="text-xs text-gray-600 mt-1">Adults</span>
                  </div>

                  <div className="flex flex-col items-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-6 h-6 border-2 border-black rounded-full p-0 transition-transform active:scale-90"
                      onClick={() => addAdditionalGuest(GuestType.CHILD)}
                    >
                      <Plus className="h-2 w-2" />
                    </Button>
                    <span className="text-xs text-gray-600 mt-1">Childs</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  id="phone"
                  label="PHONE"
                  type="text"
                  required
                  register={register}
                  error={errors?.phone?.message}
                  placeholder="Enter phone"
                />

                <FormField
                  id="email"
                  label="EMAIL"
                  type="email"
                  required
                  register={register}
                  error={errors?.email?.message}
                  placeholder="Enter email"
                />

                <CountryField
                  className="md:col-span-2"
                  selectedCountry={selectedCountry ?? ""}
                  onSelect={(code) => setValue("language", code)}
                  error={errors.language?.message}
                />

                <FormField
                  id="dateOfBirth"
                  label="DATE OF BIRTH"
                  type="date"
                  required
                  register={register}
                  error={errors?.dateOfBirth?.message}
                  placeholder="YYYY-MM-DD"
                  setValue={setValue}
                  watch={watch}
                  maxDate={new Date()}
                />
              </div>
              <div className="mt-6 space-y-6">
                {additionalGuests.some(
                  (g) => g.guestType === GuestType.ADULT,
                ) && (
                  <div>
                    <h3 className="text-md font-medium mb-4">
                      Additional Adults
                    </h3>
                    <div className="space-y-4">
                      {additionalGuests
                        .map((g, index) => ({ g, index }))
                        .filter(({ g }) => g.guestType === GuestType.ADULT)
                        .map(({ g, index }) => (
                          <div
                            key={index}
                            className="border border-black rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4"
                          >
                            <FormField
                              id={`additionalGuests.${index}.firstName`}
                              label="NAME"
                              type="text"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.firstName?.message
                              }
                              placeholder="Enter first name"
                            />
                            <FormField
                              id={`additionalGuests.${index}.lastName`}
                              label="SURNAME"
                              type="text"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.lastName?.message
                              }
                              placeholder="Enter surname"
                            />
                            <FormField
                              id={`additionalGuests.${index}.email`}
                              label="EMAIL"
                              type="email"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.email?.message
                              }
                              placeholder="Enter email"
                            />
                            <FormField
                              id={`additionalGuests.${index}.phone`}
                              label="PHONE"
                              type="text"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.phone?.message
                              }
                              placeholder="Enter phone"
                            />
                            <CountryField
                              selectedCountry={g.language ?? ""}
                              onSelect={(code) =>
                                setValue(
                                  `additionalGuests.${index}.language`,
                                  code,
                                )
                              }
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.language?.message
                              }
                            />
                            <FormField
                              id={`additionalGuests.${index}.dateOfBirth`}
                              label="DATE OF BIRTH"
                              type="date"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.dateOfBirth?.message
                              }
                              placeholder="YYYY-MM-DD"
                              setValue={setValue}
                              watch={watch}
                              maxDate={new Date()}
                            />
                            <div className="md:col-span-3 flex justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => removeAdditionalGuest(index)}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {additionalGuests.some(
                  (g) => g.guestType === GuestType.CHILD,
                ) && (
                  <div>
                    <h3 className="text-md font-medium mb-4">
                      Additional Children
                    </h3>
                    <div className="space-y-4">
                      {additionalGuests
                        .map((g, index) => ({ g, index }))
                        .filter(({ g }) => g.guestType === GuestType.CHILD)
                        .map(({ g, index }) => (
                          <div
                            key={index}
                            className="border border-black rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4"
                          >
                            <FormField
                              id={`additionalGuests.${index}.firstName`}
                              label="NAME"
                              type="text"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.firstName?.message
                              }
                              placeholder="Enter first name"
                            />
                            <FormField
                              id={`additionalGuests.${index}.lastName`}
                              label="SURNAME"
                              type="text"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.lastName?.message
                              }
                              placeholder="Enter surname"
                            />
                            <FormField
                              id={`additionalGuests.${index}.email`}
                              label="EMAIL"
                              type="email"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.email?.message
                              }
                              placeholder="Enter email"
                            />
                            <FormField
                              id={`additionalGuests.${index}.phone`}
                              label="PHONE"
                              type="text"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.phone?.message
                              }
                              placeholder="Enter phone"
                            />
                            <CountryField
                              selectedCountry={g.language ?? ""}
                              onSelect={(code) =>
                                setValue(
                                  `additionalGuests.${index}.language`,
                                  code,
                                )
                              }
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.language?.message
                              }
                            />
                            <FormField
                              id={`additionalGuests.${index}.dateOfBirth`}
                              label="DATE OF BIRTH"
                              type="date"
                              required
                              register={register}
                              error={
                                (errors as any)?.additionalGuests?.[index]
                                  ?.dateOfBirth?.message
                              }
                              placeholder="YYYY-MM-DD"
                              setValue={setValue}
                              watch={watch}
                              maxDate={new Date()}
                            />
                            <div className="md:col-span-3 flex justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => removeAdditionalGuest(index)}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-lg font-medium mb-6">Booking information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-1">
                  <RoomField
                    value={roomId}
                    onChange={(value) => setValue("roomId", value)}
                    error={errors.roomId?.message}
                    showOnlyAvailable={true}
                    startDate={new Date(arrivalDate)}
                    endDate={new Date(departureDate)}
                    labelClassName="uppercase"
                  />
                </div>
                <PaymentStatusSelect
                  value={
                    (paymentStatus as BillingPaymentStatus) ||
                    BillingPaymentStatus.NOT_REQUIRED
                  }
                  onChange={(value) =>
                    setValue("paymentStatus", value as BillingPaymentStatus)
                  }
                />

                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    ARRIVAL
                  </label>
                  <DatePicker
                    maxDate={new Date(departureDate)}
                    date={new Date(arrivalDate)}
                    onDateChange={(date) =>
                      // setValue("arrivalDate", date.toISOString() || "")
                      handleDateChane("arrival", date)
                    }
                    placeholder="Choose date"
                    className={`border-2 w-full rounded-lg focus:ring-0 focus:border-black hover:border-black ${errors.arrivalDate ? "border-red-500" : "border-black"}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    DEPARTURE
                  </label>
                  <DatePicker
                    minDate={
                      new Date(
                        new Date(arrivalDate).setDate(
                          new Date(arrivalDate).getDate() + 1,
                        ),
                      )
                    }
                    date={new Date(departureDate)}
                    onDateChange={(date) =>
                      // setValue("departureDate", date.toISOString() || "")
                      handleDateChane("departure", date)
                    }
                    placeholder="Choose date"
                    className={`border-2 w-full focus:ring-0 rounded-lg focus:border-black hover:border-black ${errors.departureDate ? "border-red-500" : "border-black"}`}
                  />
                </div>
              </div>
            </div>

            {/* Pre-check-in section */}
            <div className="mb-8">
              <h2 className="text-lg font-medium mb-2">Pre-check-in</h2>

              <label className="block text-sm font-medium text-black mb-2">
                Uploaded Documents
              </label>
              <div className={"col-span-2 flex flex-row gap-4"}>
                {documents?.map((document, index) => (
                  <DocumentItem
                    className={"max-h-[100px] min-h-full"}
                    key={index}
                    id={index}
                    name={document.file?.name || ""}
                    remove={(id) => removeDocument(id)}
                    fileId={document.fileId}
                    type={document.type}
                  />
                ))}
                <AddDocumentModal
                  trigger={
                    <div className={"flex items-end"}>
                      <div className="w-12 h-12 bg-gray-100 border-2 border-dashed border-black rounded-lg flex items-center justify-center">
                        <Upload className="h-5 w-5 text-gray-400" />
                      </div>
                      <Plus className="absolute -bottom-1 -right-1 bg-black text-white rounded-full p-0 hover:bg-gray-800 h-2.5 w-2.5" />
                    </div>
                  }
                  onSubmit={handleDocumentSubmit}
                />
              </div>

              <div className="space-y-4 mt-4">
                <FormField
                  id="comments"
                  label="COMMENTS"
                  type="text"
                  required
                  register={register}
                  error={errors?.comments?.message}
                  placeholder="Enter comments"
                />
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t">
              <Button type="submit" disabled={isSubmitting} className="px-8">
                {isSubmitting ? "Creating..." : "Create Reservation"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
